import DateRangeInput from 'pureUi/DateRangeInput';
import { DatePickerStateProvider, IDatePickerSateParams } from 'pureUi/providers/DatePickerStateProvider';
import React, { useCallback, useState, useMemo } from 'react';
import { GuestAgesPicker } from 'ui/GuestAgesPicker/GuestAgesPicker';
import * as BreakdownActions from 'store/modules/bookingManager/subdomains/breakdown/actions';
import { useDispatch } from 'react-redux';
import { subYears } from 'date-fns';

interface IPeopleAndDatesInputsProps {
  guestAges: {
    numberOfAdults: number;
    agesOfAllChildren?: number[];
  };
  selectedDates: string[];
  disabled?: boolean;
  isManualEditMode?: boolean;
}

export const PeopleAndDatesInputs: React.FC<IPeopleAndDatesInputsProps> = ({
  guestAges,
  disabled,
  selectedDates,
  isManualEditMode = false,
}) => {
  const dispatch = useDispatch();
  const [prevGuestAges, setPrevGuestAges] = useState(guestAges);

  const handleCloseGuestAgesPicker = useCallback(
    (visible: boolean) => {
      if (
        visible &&
        (guestAges.numberOfAdults !== prevGuestAges.numberOfAdults ||
          JSON.stringify(guestAges.agesOfAllChildren) !== JSON.stringify(prevGuestAges.agesOfAllChildren))
      ) {
        // In manual edit mode, don't trigger booking builder request
        if (!isManualEditMode) {
          dispatch(BreakdownActions.editAccommodationBookingBuildRequestAction());
        }
        dispatch(BreakdownActions.editAccommodationSetHasTochedAction());
        setPrevGuestAges(guestAges);
      }
    },
    [dispatch, guestAges, prevGuestAges, isManualEditMode]
  );

  const handleSetGuestAges = useCallback(
    (numberOfAdults: number, agesOfAllChildren: number[]) => {
      dispatch(
        BreakdownActions.editAccommodationSetGuestAgesAction({
          numberOfAdults,
          agesOfAllChildren: agesOfAllChildren || [],
        })
      );
    },
    [dispatch]
  );

  const handleEditAccommodationDateRangeChange = useCallback(
    (dates: string[]) => {
      dispatch(BreakdownActions.editAccommodationDateRangeChangeAction(dates));
      // In manual edit mode, don't trigger booking builder request
      if (!isManualEditMode) {
        dispatch(BreakdownActions.editAccommodationBookingBuildRequestAction());
      }
      dispatch(BreakdownActions.editAccommodationSetHasTochedAction());
    },
    [dispatch, isManualEditMode]
  );

  // Date constraints for manual mode
  const oneYearAgo = useMemo(() => subYears(new Date(), 1), []);
  const today = useMemo(() => new Date(), []);

  return (
    <div className="people-and-dates-inputs flex gap-5">
      <label className="text-xs block mb-6 text-gray-500 w-full">
        <span className="block mb-2 font-bold">People *</span>
        <GuestAgesPicker
          numberOfAdults={guestAges.numberOfAdults}
          agesOfAllChildren={guestAges.agesOfAllChildren}
          onUpdate={handleSetGuestAges}
          maxChildAge={16}
          onClose={handleCloseGuestAgesPicker}
          disabled={disabled}
        />
      </label>
      <label className="text-xs block mb-6 text-gray-500 w-full">
        <span className="block mb-2 font-bold">Dates *</span>
        <DatePickerStateProvider
          defaultSelectedDates={selectedDates}
          onDateChange={handleEditAccommodationDateRangeChange}
          render={(params: IDatePickerSateParams) => {
            // In manual mode: start date limited to past, end date can go into future
            const effectiveMaxDate = isManualEditMode
              ? (params.dateSelectionInProgress ? undefined : today.toISOString())
              : undefined;
            
            return (
              <DateRangeInput
                displayString={params.displayString}
                currentDate={params.datePickerCurrentDate}
                totalNights={params.totalNights}
                selectedDates={params.selectedDates}
                onDayClick={params.handleDayClick}
                onDayMouseOver={params.handleDateMouseOver}
                showDatePicker={params.showDatePicker}
                onNextClick={params.incrementDate}
                onPrevClick={params.decrementDate}
                onMouseDown={params.toggleDatePicker}
                onClickOutside={params.hideDatePicker}
                placeholder={'Select Dates...'}
                showTotalNights
                shortNightsVariant
                noPortal
                disabled={disabled}
                enablePastDates={isManualEditMode}
                minDate={isManualEditMode ? oneYearAgo.toISOString() : undefined}
                maxDate={effectiveMaxDate}
              />
            );
          }}
        />
      </label>
    </div>
  );
};
